import type { PaginationQuery } from '../../../types/pagination';
import { appendPaginationQuery } from '../../../types/pagination';
import type { ApiClient } from '../client';
import type { CreateCommunicationTemplateRequest, CreateHostedMessageLinksRequest, CreateHostedMessageLinksResponse, ListCommunicationTemplatesResponse, ListDeliveryLogsQuery, ListDeliveryLogsResponse, PreviewWhatsAppAudienceResponse, SendCommunicationMessageRequest, SendCommunicationMessageResponse, SendWhatsAppBulkRequest, Uuid, WhatsAppAudienceRequest, CommunicationSetupStatus, SetupPlatformEmailRequest, SetupPlatformEmailResponse, RegisterWhatsAppPhoneRequest, RegisterWhatsAppPhoneResponse, VerifyWhatsAppPhoneRequest, VerifyWhatsAppPhoneResponse, UpsertWhatsAppMetaRequest, UpsertWhatsAppMetaResponse, TestWhatsAppMetaResponse, DisconnectWhatsAppMetaResponse, CommunicationCreditsResponse, CreateCommunicationTopUpOrderRequest, CreateCommunicationTopUpOrderResponse, VerifyCommunicationTopUpRequest, VerifyCommunicationTopUpResponse, AddCommunicationCreditsRequest, AddCommunicationCreditsResponse, InitiateCommunicationPaytmTopUpRequest, InitiateCommunicationPaytmTopUpResponse, CompleteCommunicationPaytmTopUpRequest, CompleteCommunicationPaytmTopUpResponse, UpdateCommunicationTemplateRequest, UpdateCommunicationTemplateResponse, CommunicationUsageQuery, CommunicationUsageResponse, ListConversationsResponse, ListInboxMessagesResponse, ReplyConversationRequest, ReplyConversationResponse } from '../types';

export function bindCommunication(client: ApiClient) {
  return {
async listCommunicationTemplates(channel?: string) {
    const q = channel ? `?channel=${encodeURIComponent(channel)}` : '';
    return client.request<ListCommunicationTemplatesResponse>(`/communication/templates${q}`);
  },

async createCommunicationTemplate(body: CreateCommunicationTemplateRequest) {
    return client.request<{ template_id: Uuid }>('/communication/templates', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async updateCommunicationTemplate(templateId: Uuid, body: UpdateCommunicationTemplateRequest) {
    return client.request<UpdateCommunicationTemplateResponse>(
      `/communication/templates/${encodeURIComponent(templateId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      }
    );
  },

async deleteCommunicationTemplate(templateId: Uuid) {
    return client.request<void>(`/communication/templates/${encodeURIComponent(templateId)}`, {
      method: 'DELETE',
    });
  },

async sendCommunicationMessage(body: SendCommunicationMessageRequest) {
    return client.request<SendCommunicationMessageResponse>('/communication/send', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async previewWhatsAppAudience(body: WhatsAppAudienceRequest) {
    return client.request<PreviewWhatsAppAudienceResponse>('/communication/whatsapp/preview-audience', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async sendWhatsAppBulk(body: SendWhatsAppBulkRequest) {
    return client.request<SendCommunicationMessageResponse>('/communication/whatsapp/send', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async createWhatsAppHostedLinks(body: CreateHostedMessageLinksRequest) {
    return client.request<CreateHostedMessageLinksResponse>('/communication/whatsapp/hosted-links', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listDeliveryLogs(params?: ListDeliveryLogsQuery) {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    if (params?.status) qs.set('status', params.status);
    const query = qs.toString();
    return client.request<ListDeliveryLogsResponse>(
      query ? `/communication/delivery-logs?${query}` : '/communication/delivery-logs'
    );
  },

async retryDelivery(deliveryLogId: string) {
    return client.request<{ delivery_log_id: Uuid; status: string; attempt_count: number }>(
      '/communication/retry',
      {
        method: 'POST',
        body: JSON.stringify({ delivery_log_id: deliveryLogId }),
      }
    );
  },

async getCommunicationSetup() {
    return client.request<CommunicationSetupStatus>('/communication/setup');
  },

async setupPlatformEmail(body?: SetupPlatformEmailRequest) {
    return client.request<SetupPlatformEmailResponse>('/communication/setup/email/platform', {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    });
  },

async registerWhatsAppPhone(body: RegisterWhatsAppPhoneRequest) {
    return client.request<RegisterWhatsAppPhoneResponse>('/communication/setup/whatsapp/register', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async verifyWhatsAppPhone(body: VerifyWhatsAppPhoneRequest) {
    return client.request<VerifyWhatsAppPhoneResponse>('/communication/setup/whatsapp/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async upsertWhatsAppMeta(body: UpsertWhatsAppMetaRequest) {
    return client.request<UpsertWhatsAppMetaResponse>('/communication/setup/whatsapp/meta', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

async testWhatsAppMeta() {
    return client.request<TestWhatsAppMetaResponse>('/communication/setup/whatsapp/meta/test', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

async disconnectWhatsAppMeta() {
    return client.request<DisconnectWhatsAppMetaResponse>('/communication/setup/whatsapp/meta', {
      method: 'DELETE',
    });
  },

async getCommunicationCredits() {
    return client.request<CommunicationCreditsResponse>('/communication/credits');
  },

async createCommunicationTopUpOrder(body: CreateCommunicationTopUpOrderRequest) {
    return client.request<CreateCommunicationTopUpOrderResponse>('/communication/credits/top-up/order', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async verifyCommunicationTopUp(body: VerifyCommunicationTopUpRequest) {
    return client.request<VerifyCommunicationTopUpResponse>('/communication/credits/top-up/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async addCommunicationCredits(body: AddCommunicationCreditsRequest) {
    return client.request<AddCommunicationCreditsResponse>('/communication/credits/add', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async initiateCommunicationPaytmTopUp(body: InitiateCommunicationPaytmTopUpRequest) {
    return client.request<InitiateCommunicationPaytmTopUpResponse>('/communication/credits/paytm/initiate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async completeCommunicationPaytmTopUp(body: CompleteCommunicationPaytmTopUpRequest) {
    return client.request<CompleteCommunicationPaytmTopUpResponse>('/communication/credits/paytm/complete', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async getCommunicationUsage(params?: CommunicationUsageQuery) {
    const qs = new URLSearchParams();
    if (params?.granularity) qs.set('granularity', params.granularity);
    if (params?.from_date) qs.set('from_date', params.from_date);
    if (params?.to_date) qs.set('to_date', params.to_date);
    if (params?.channel) qs.set('channel', params.channel);
    const query = qs.toString();
    return client.request<CommunicationUsageResponse>(
      query ? `/communication/usage?${query}` : '/communication/usage'
    );
  },

async downloadCommunicationUsageReport(params?: CommunicationUsageQuery): Promise<Blob> {
    const qs = new URLSearchParams();
    if (params?.granularity) qs.set('granularity', params.granularity);
    if (params?.from_date) qs.set('from_date', params.from_date);
    if (params?.to_date) qs.set('to_date', params.to_date);
    if (params?.channel) qs.set('channel', params.channel);
    const query = qs.toString();
    const path = query ? `/communication/usage/report?${query}` : '/communication/usage/report';
    const response = await fetch(`${client.baseUrl}${path}`, {
      method: 'GET',
      headers: client.authHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Usage report download failed: ${response.status}`);
    }
    return response.blob();
  },

async listCommunicationConversations(params?: PaginationQuery) {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    const query = qs.toString();
    return client.request<ListConversationsResponse>(
      query ? `/communication/conversations?${query}` : '/communication/conversations'
    );
  },

async listConversationMessages(conversationId: Uuid | string, params?: PaginationQuery) {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    const query = qs.toString();
    const base = `/communication/conversations/${encodeURIComponent(String(conversationId))}/messages`;
    return client.request<ListInboxMessagesResponse>(query ? `${base}?${query}` : base);
  },

async replyConversation(conversationId: Uuid | string, body: ReplyConversationRequest) {
    return client.request<ReplyConversationResponse>(
      `/communication/conversations/${encodeURIComponent(String(conversationId))}/reply`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
  },
  };
}
